import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { formatEURDecimal } from "../lib/format";
import { Plus, Upload, Loader2, Trash2, Pencil, Sparkles, X, Save, FileText } from "lucide-react";
import { toast } from "sonner";

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const CATEGORIAS = ["Nóminas","Servicios","Software","Viajes Corp","Seguros e Intereses","Otros"];
const eur = (v) => formatEURDecimal(v);

export default function GastosFijosEstructura() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get(`/gastos-fijos-estructura?year=${year}`).then(r => setData(r.data)).catch(e => toast.error(e?.response?.data?.detail || "Error")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const onUploadPDF = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/gastos-fijos-estructura/extract-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      const x = r.data;
      const fecha = x.fecha_factura || "";
      const mes = fecha ? parseInt(fecha.slice(5, 7)) : new Date().getMonth() + 1;
      const yy = fecha ? parseInt(fecha.slice(0, 4)) : year;
      setEditing({
        mes, year: yy || year, categoria: x.categoria_sugerida || "Otros",
        proveedor: x.proveedor || "", concepto: x.concepto || "",
        numero_factura: x.numero_factura || "", fecha_factura: fecha,
        base_imponible: x.base_imponible || 0, iva: x.iva || 0,
        importe: x.importe_total || 0, notas: "",
      });
      toast.success("PDF procesado por IA. Revisa y guarda.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error extrayendo PDF");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!editing) return;
    try {
      const payload = { ...editing, base_imponible: parseFloat(editing.base_imponible) || 0, iva: parseFloat(editing.iva) || 0, importe: parseFloat(editing.importe) || 0, mes: parseInt(editing.mes), year: parseInt(editing.year) };
      if (editing.id) await api.put(`/gastos-fijos-estructura/${editing.id}`, payload);
      else await api.post("/gastos-fijos-estructura", payload);
      toast.success("Guardado");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Borrar este gasto?")) return;
    await api.delete(`/gastos-fijos-estructura/${id}`); load();
  };

  if (loading) return <div className="p-10 text-center text-sm text-[var(--ink-muted)]">Cargando…</div>;
  if (!data) return null;

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-6" data-testid="gastos-fijos-estructura-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Estructura</p>
          <h1 className="font-display text-3xl font-black tracking-tight">Gastos Fijos · {year}</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-1">Cuadro mensual por categoría. Sube el PDF de la factura y la IA rellena el formulario.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="border border-[var(--border)] rounded px-3 py-2 text-sm bg-white" data-testid="gf-year">
            {[year-1, year, year+1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <input ref={fileRef} type="file" accept=".pdf" onChange={onUploadPDF} className="hidden" data-testid="gf-upload-input"/>
          <button data-testid="gf-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 border border-[var(--border)] bg-white text-sm px-3 py-2 rounded disabled:opacity-40">
            {uploading ? <Loader2 size={14} className="animate-spin"/> : <><Sparkles size={14}/> <Upload size={14}/></>} Subir PDF (IA)
          </button>
          <button data-testid="gf-new-btn" onClick={() => setEditing({ mes: new Date().getMonth()+1, year, categoria: "Otros", proveedor: "", concepto: "", numero_factura: "", fecha_factura: "", base_imponible: 0, iva: 0, importe: 0, notas: "" })} className="inline-flex items-center gap-2 bg-black text-white text-sm px-3 py-2 rounded">
            <Plus size={14}/> Manual
          </button>
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <div className="bg-white border border-[var(--border)] rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-black">{editing.id ? "Editar gasto" : "Nuevo gasto"}</h3>
            <button onClick={() => setEditing(null)} className="text-[var(--ink-muted)] hover:text-[var(--ink)]"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
            <Field label="Mes"><select value={editing.mes} onChange={e => setEditing({...editing, mes: parseInt(e.target.value)})} className="w-full border rounded px-2 py-1.5">{MESES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}</select></Field>
            <Field label="Año"><input type="number" value={editing.year} onChange={e => setEditing({...editing, year: e.target.value})} className="w-full border rounded px-2 py-1.5"/></Field>
            <Field label="Categoría"><select value={editing.categoria} onChange={e => setEditing({...editing, categoria: e.target.value})} className="w-full border rounded px-2 py-1.5">{CATEGORIAS.map(c => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Proveedor"><input value={editing.proveedor} onChange={e => setEditing({...editing, proveedor: e.target.value})} className="w-full border rounded px-2 py-1.5"/></Field>
            <Field label="Nº factura"><input value={editing.numero_factura} onChange={e => setEditing({...editing, numero_factura: e.target.value})} className="w-full border rounded px-2 py-1.5"/></Field>
            <Field label="Fecha factura"><input type="date" value={editing.fecha_factura} onChange={e => setEditing({...editing, fecha_factura: e.target.value})} className="w-full border rounded px-2 py-1.5"/></Field>
            <Field label="Concepto" span="md:col-span-3"><input value={editing.concepto} onChange={e => setEditing({...editing, concepto: e.target.value})} className="w-full border rounded px-2 py-1.5"/></Field>
            <Field label="Base"><input type="number" step="0.01" value={editing.base_imponible} onChange={e => setEditing({...editing, base_imponible: e.target.value})} className="w-full border rounded px-2 py-1.5"/></Field>
            <Field label="IVA"><input type="number" step="0.01" value={editing.iva} onChange={e => setEditing({...editing, iva: e.target.value})} className="w-full border rounded px-2 py-1.5"/></Field>
            <Field label="Total"><input type="number" step="0.01" value={editing.importe} onChange={e => setEditing({...editing, importe: e.target.value})} className="w-full border rounded px-2 py-1.5 font-semibold"/></Field>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setEditing(null)} className="text-sm px-3 py-2 border rounded">Cancelar</button>
            <button data-testid="gf-save" onClick={save} className="inline-flex items-center gap-2 bg-black text-white text-sm px-4 py-2 rounded"><Save size={14}/> Guardar</button>
          </div>
        </div>
      )}

      {/* Cuadro anual */}
      <div className="bg-white border border-[var(--border)] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)] text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-num">
            <tr>
              <th className="text-left px-4 py-2.5">Categoría</th>
              {MESES.map(m => <th key={m} className="text-right px-2 py-2.5">{m}</th>)}
              <th className="text-right px-4 py-2.5 border-l">Total</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORIAS.map(cat => (
              <tr key={cat} className="border-t hover:bg-[var(--hover)]">
                <td className="px-4 py-2 font-medium">{cat}</td>
                {MESES.map((_, i) => {
                  const v = data.cuadro[cat]?.[i+1] || 0;
                  return <td key={i} className={`text-right px-2 py-2 font-mono-num ${v ? "" : "text-[var(--ink-muted)]"}`}>{v ? eur(v) : "—"}</td>;
                })}
                <td className="text-right px-4 py-2 font-mono-num font-semibold border-l">{eur(data.total_por_categoria[cat])}</td>
              </tr>
            ))}
            <tr className="border-t bg-[var(--bg)] font-semibold">
              <td className="px-4 py-2.5">TOTAL</td>
              {data.total_por_mes.map((v, i) => <td key={i} className="text-right px-2 py-2.5 font-mono-num">{v ? eur(v) : "—"}</td>)}
              <td className="text-right px-4 py-2.5 font-mono-num border-l">{eur(data.total_year)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Entries table */}
      <div className="bg-white border border-[var(--border)] rounded-lg">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-display text-base font-black">Movimientos ({data.entries.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-num bg-[var(--bg)]">
              <tr>
                <th className="text-left px-3 py-2">Mes</th><th className="text-left px-3 py-2">Categoría</th><th className="text-left px-3 py-2">Proveedor</th><th className="text-left px-3 py-2">Concepto</th><th className="text-left px-3 py-2">Nº fact.</th><th className="text-right px-3 py-2">Base</th><th className="text-right px-3 py-2">IVA</th><th className="text-right px-3 py-2">Total</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.entries.length === 0 && <tr><td colSpan={9} className="text-center text-[var(--ink-muted)] py-6">Sin gastos aún. Sube un PDF o añade manual.</td></tr>}
              {data.entries.map(e => (
                <tr key={e.id} className="border-t hover:bg-[var(--hover)]" data-testid={`gf-row-${e.id}`}>
                  <td className="px-3 py-2 font-mono-num text-xs">{MESES[(e.mes||1)-1]}</td>
                  <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-[var(--bg)] border">{e.categoria}</span></td>
                  <td className="px-3 py-2 truncate max-w-[220px]">{e.proveedor || "—"}</td>
                  <td className="px-3 py-2 truncate max-w-[280px]">{e.concepto || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono-num">{e.numero_factura || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{eur(e.base_imponible)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-[var(--ink-muted)]">{eur(e.iva)}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">{eur(e.importe)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(e)} className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"><Pencil size={13}/></button>
                    <button onClick={() => remove(e.id)} className="p-1 text-[var(--ink-muted)] hover:text-rose-600"><Trash2 size={13}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, span }) {
  return (
    <div className={span || ""}>
      <label className="block text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-num">{label}</label>
      {children}
    </div>
  );
}
